import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Link as LinkIcon, Check, Copy, ArrowRightLeft, Music, User, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { fetchWithTimeout } from '../App'; // We might need to make fetchWithTimeout accessible, or just use fetch

interface CustomChallengeModalProps {
  onClose: () => void;
  isLightMode: boolean;
}

export function CustomChallengeModal({ onClose, isLightMode }: CustomChallengeModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [startArtist, setStartArtist] = useState<any>(null);
  const [targetArtist, setTargetArtist] = useState<any>(null);
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/searchArtists?q=${encodeURIComponent(query)}`);
        if (res.ok) {
          const data = await res.json();
          setResults(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 400);
    
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [query]);

  const selectArtist = (artist: any) => {
    if (step === 1) {
      setStartArtist(artist);
      setStep(2);
      setQuery('');
      setResults([]);
    } else if (step === 2) {
      setTargetArtist(artist);
      setStep(3);
    }
  };

  const getEncodedParam = () => {
    if (!startArtist || !targetArtist) return '';
    const payload = `${startArtist.name}~${targetArtist.name}`;
    // Safe base64 encoding for unicode, trimmed of trailing padding for a shorter URL
    return btoa(encodeURIComponent(payload).replace(/%([0-9A-F]{2})/g,
        function toSolidBytes(match, p1) {
            return String.fromCharCode(Number('0x' + p1));
    })).replace(/=+$/, '');
  };

  const getShareLink = () => {
    const encoded = getEncodedParam();
    if (!encoded) return '';
    return `${window.location.origin}/c/${encoded}`;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const handlePlayChallenge = () => {
    const encoded = getEncodedParam();
    if (!encoded) return;
    onClose();
    navigate(`/c/${encoded}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className={cn(
          "relative w-full max-w-md rounded-3xl p-6 sm:p-8 border shadow-2xl overflow-hidden flex flex-col max-h-[85vh] transition-colors",
          isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#222222] border-white/10"
        )}
      >
        <button 
          onClick={onClose} 
          className={cn(
            "absolute top-5 right-5 p-2 rounded-full transition-colors z-10",
            isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
          )}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className={cn("text-2xl font-bold mb-1", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
          Create Custom Challenge
        </h2>
        <p className={cn("text-sm mb-6", isLightMode ? "text-gray-600" : "text-gray-400")}>
          Challenge your friends to connect two artists of your choice.
        </p>

        {step < 3 ? (
          <div className="flex-1 flex flex-col min-h-0">
            <div className={cn(
              "flex items-center gap-3 p-3 rounded-2xl border mb-4 shadow-inner",
              isLightMode ? "bg-white border-black/10 text-black" : "bg-black/30 border-white/10 text-white"
            )}>
              <Search className="w-5 h-5 text-gray-400 ml-1" />
              <input 
                type="text"
                autoFocus
                placeholder={step === 1 ? "Search Start Artist..." : "Search Target Artist..."}
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full bg-transparent outline-none placeholder:text-gray-500 font-medium"
              />
            </div>

            <div className={cn(
              "flex-1 overflow-y-auto rounded-2xl custom-scrollbar",
              isLightMode ? "bg-white/50" : "bg-black/20"
            )}>
              {loading && results.length === 0 ? (
                <div className="p-8 text-center text-sm font-medium text-gray-500 animate-pulse">
                  Searching artists...
                </div>
              ) : results.length > 0 ? (
                <div className="p-2 space-y-1">
                  {results.map(artist => (
                    <button
                      key={artist.id}
                      onClick={() => selectArtist(artist)}
                      className={cn(
                        "w-full flex items-center gap-4 p-3 rounded-xl transition-all text-left",
                        isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                      )}
                    >
                      {artist.imageUrl ? (
                        <img src={artist.imageUrl} alt={artist.name} className="w-12 h-12 rounded-full object-cover bg-gray-200 dark:bg-gray-800" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 font-bold truncate">{artist.name}</div>
                    </button>
                  ))}
                </div>
              ) : query && !loading ? (
                <div className="p-8 text-center text-sm font-medium text-gray-500">
                  No artists found
                </div>
              ) : (
                <div className="p-8 text-center text-sm font-medium text-gray-500 flex flex-col items-center gap-3">
                  <Music className="w-8 h-8 opacity-20" />
                  <span>{step === 1 ? "Who will they start with?" : "Who is their target?"}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
            
            <div className={cn(
              "w-full p-6 rounded-3xl border mb-8 flex flex-col items-center gap-4",
              isLightMode ? "bg-white border-black/10 shadow-sm" : "bg-black/40 border-white/10"
            )}>
              <div className="flex items-center gap-4 w-full">
                {startArtist?.imageUrl ? (
                  <img src={startArtist.imageUrl} alt="" className="w-16 h-16 rounded-full object-cover shadow-md" />
                ) : <div className="w-16 h-16 rounded-full bg-gray-800 shrink-0" />}
                <div className={cn("flex-1 text-left font-bold text-lg truncate", isLightMode ? "text-black" : "text-white")}>
                  {startArtist?.name}
                </div>
              </div>
              
              <ArrowRightLeft className="w-6 h-6 text-[#ff0080] rotate-90 my-2" />
              
              <div className="flex items-center gap-4 w-full flex-row-reverse text-right">
                {targetArtist?.imageUrl ? (
                  <img src={targetArtist.imageUrl} alt="" className="w-16 h-16 rounded-full object-cover shadow-md" />
                ) : <div className="w-16 h-16 rounded-full bg-gray-800 shrink-0" />}
                <div className={cn("flex-1 font-bold text-lg truncate", isLightMode ? "text-black" : "text-white")}>
                  {targetArtist?.name}
                </div>
              </div>
            </div>

            <div className="w-full flex flex-col gap-3">
              <button
                onClick={handlePlayChallenge}
                className={cn(
                  "w-full py-4 rounded-2xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 transition-all shadow-lg",
                  "bg-[#ff0080] text-white shadow-[#ff0080]/20 hover:scale-[1.02] active:scale-[0.98]"
                )}
              >
                <Play className="w-5 h-5 fill-current" /> Play Challenge
              </button>

              <button
                onClick={copyLink}
                className={cn(
                  "w-full py-4 rounded-2xl font-bold text-base sm:text-lg flex items-center justify-center gap-2 transition-all border",
                  copied 
                    ? "bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20 scale-[0.98]" 
                    : isLightMode 
                      ? "bg-white border-black/10 text-[#1a1a1a] hover:bg-gray-50 shadow-sm"
                      : "bg-white/10 border-white/10 text-white hover:bg-white/15"
                )}
              >
                {copied ? (
                  <><Check className="w-5 h-5" /> Copied to Clipboard!</>
                ) : (
                  <><LinkIcon className="w-5 h-5" /> Copy Invite Link</>
                )}
              </button>
            </div>
            
            <button 
              onClick={() => {
                setStep(1);
                setStartArtist(null);
                setTargetArtist(null);
                setQuery('');
              }}
              className="mt-5 text-sm font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Start Over
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
